/**
 * studentRoutes.jsx — Routes publiques/élèves + Magazine + Piliers SEO.
 *
 * Toutes ces routes sont accessibles aux visiteurs et aux élèves connectés.
 * Les enseignants/admins connectés sur une route élève seront redirigés
 * vers /enseignant via `StudentOnlyRoute`.
 */
import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import { withLayout, withStudentLayout } from '../lib/routeHelpers';
import { ProtectedRoute } from '../components/ProtectedRoute';

// Pages élèves / publiques
import Home from '../pages/Home';
import Formation from '../pages/Formation';
import MobilitesUrbaines from '../pages/MobilitesUrbaines';
import EspaceEleve from '../pages/EspaceEleve';
import EtmIssuePage from '../pages/EtmIssuePage';
import EtmSatellitePage from '../pages/EtmSatellitePage';
import CampagneMotardsExperimentes from '../pages/CampagneMotardsExperimentes';
import ARevoir from '../pages/ARevoir';
import ProgrammeETM from '../pages/ProgrammeETM';
import Lexique from '../pages/Lexique';
import PanneauxGallery from '../pages/PanneauxGallery';
import MonLivretMoto from '../pages/MonLivretMoto';
import LivretApprentissageA from '../pages/LivretApprentissageA';
import SeanceRecap from '../pages/SeanceRecap';
import PreparationCCS from '../pages/PreparationCCS';
import Videos from '../pages/Videos';
import ShortsPage from '../pages/ShortsPage';
import ShortsProposerPage from '../pages/ShortsProposerPage';
import VideosConditionsPage from '../pages/VideosConditionsPage';
import DemosInteractives from '../pages/DemosInteractives';
import Annuaire from '../pages/Annuaire';
import MotoEcolesHub from '../pages/moto-ecoles/MotoEcolesHub';
import MotoEcolesDept from '../pages/moto-ecoles/MotoEcolesDept';
import MotoEcolesVille from '../pages/moto-ecoles/MotoEcolesVille';
import MotoEcoleGerer from '../pages/moto-ecoles/MotoEcoleGerer';
import MentionsAnnuaire from '../pages/moto-ecoles/MentionsAnnuaire';
import OppositionConfirmer from '../pages/moto-ecoles/OppositionConfirmer';
import PlateauProgression from '../pages/PlateauProgression';
import Formation125Progression from '../pages/Formation125Progression';
import PasserelleA2AProgression from '../pages/PasserelleA2AProgression';
import RapportCriteres from '../pages/RapportCriteres';
import Formations2Roues from '../pages/Formations2Roues';
import Livret125 from '../pages/fiches/Livret125';
import LivretAM from '../pages/fiches/LivretAM';
import LivretPasserelleA2A from '../pages/fiches/LivretPasserelleA2A';
import ASSR from '../pages/ASSR';
import Bibliotheque from '../pages/Bibliotheque';
import NouveauFormat from '../pages/NouveauFormat';
import SeoShortRoute from '../pages/SeoShortRoute';

// Magazine
import MagazineHub from '../pages/MagazineHub';
import MagazineIndex from '../pages/MagazineIndex';
import MagazineCasque from '../pages/MagazineCasque';
import MagazineArticlePage from '../pages/MagazineArticlePage';
import EnquetesListPage from '../pages/EnquetesListPage';

// Pages piliers SEO
import PillarCodeMotoGratuit from '../pages/pillars/PillarCodeMotoGratuit';
import PillarPermisA1 from '../pages/pillars/PillarPermisA1';
import PillarPermisA2 from '../pages/pillars/PillarPermisA2';
import PillarBsrAm from '../pages/pillars/PillarBsrAm';
import PillarCirculationMoto from '../pages/pillars/PillarCirculationMoto';
import PillarPasserelleA2A from '../pages/pillars/PillarPasserelleA2A';
import PillarPermis125PermisB from '../pages/pillars/PillarPermis125PermisB';
import PillarL5eRyker from '../pages/pillars/PillarL5eRyker';

// Épreuves pratiques (élève + enseignant)
import EpreuvesHub from '../pages/epreuves/Hub';
import EpreuvesPlateau from '../pages/epreuves/Plateau';
import EpreuvesCirculation from '../pages/epreuves/Circulation';
import EpreuvesTrajectoires from '../pages/epreuves/Trajectoires';

// Auth + légal
import Login from '../pages/Login';
import Register from '../pages/Register';
import ForgotPassword from '../pages/ForgotPassword';
import TeacherApplication from '../pages/TeacherApplication';
import MentionsLegales from '../pages/MentionsLegales';
import CharteEditoriale from '../pages/CharteEditoriale';
import Confidentialite from '../pages/Confidentialite';
import SanctionsBaremes from '../pages/SanctionsBaremes';
import RessourcesOfficielles from '../pages/RessourcesOfficielles';
import Contact from '../pages/Contact';
import Newsletter from '../pages/Newsletter';
import NewsletterUnsubscribe from '../pages/NewsletterUnsubscribe';

// Espace Voiture (fusion codevoiture.fr → code2roues.fr, juil. 2026)
import VoitureHub from '../pages/voiture/VoitureHub';
import VoitureMagazine from '../pages/voiture/VoitureMagazine';
import VoitureArticle from '../pages/voiture/VoitureArticle';

// Back-office (Phase 29 — import codevoiture.fr)
import BoHubPage from '../pages/bo/BoHubPage';
import BoSecretariatElevesPage from '../pages/bo/BoSecretariatElevesPage';
import BoSecretariatEnseignantsPage from '../pages/bo/BoSecretariatEnseignantsPage';
import BoSecretariatPlanningPage from '../pages/bo/BoSecretariatPlanningPage';
import BoMyPlanningPage from '../pages/bo/BoMyPlanningPage';
import BoEleveDashboardPage from '../pages/bo/BoEleveDashboardPage';
import BoAdminInboxPage from '../pages/bo/BoAdminInboxPage';
import PartagePage from '../pages/PartagePage';
import MoniteurQrPage from '../pages/MoniteurQrPage';
import RessourcesPedagogiques from '../pages/RessourcesPedagogiques';
import LivretAMLecteur from '../pages/LivretAMLecteur';
import LivretPlateauBookPublic from '../pages/fiches/LivretPlateauBookPublic';
import LivretPlateauPrint from '../pages/fiches/LivretPlateauPrint';
import LivretCirculationBookPublic from '../pages/fiches/LivretCirculationBookPublic';
import LivretCirculationPrint from '../pages/fiches/LivretCirculationPrint';
import PremiereCirculationA2 from '../pages/cours/PremiereCirculationA2';
import DeuxiemeCirculationA2 from '../pages/cours/DeuxiemeCirculationA2';
import TroisiemeCirculationA2 from '../pages/cours/TroisiemeCirculationA2';
import QuatriemeCirculationA2 from '../pages/cours/QuatriemeCirculationA2';
import CinquiemeCirculationA2 from '../pages/cours/CinquiemeCirculationA2';
import SixiemeCirculationA2 from '../pages/cours/SixiemeCirculationA2';
import PremierePlateauA2 from '../pages/cours/PremierePlateauA2';
import DeuxiemePlateauA2 from '../pages/cours/DeuxiemePlateauA2';
import TroisiemePlateauA2 from '../pages/cours/TroisiemePlateauA2';
import QuatriemePlateauA2 from '../pages/cours/QuatriemePlateauA2';
import PermisAmHub from '../pages/permis-am/Hub';
import HubVoiturette from '../pages/permis-am/voiturette/HubVoiturette';
import SequenceVoiturette from '../pages/permis-am/voiturette/SequenceVoiturette';
import ThemesAM from '../pages/permis-am/ThemesAM';
import LivretTopolinoAM from '../pages/livrets/LivretTopolinoAM';
import LivretParcoursAM from '../pages/livrets/LivretParcoursAM';
import LivretVoiturette8h from '../pages/livrets/LivretVoiturette8h';
import PermisAmJournee1 from '../pages/permis-am/PermisAmJournee1';
import PermisAmJournee2 from '../pages/permis-am/PermisAmJournee2';
import PermisAmSensibilisation from '../pages/permis-am/PermisAmSensibilisation';
import ProgrammeOfficielAM from '../pages/permis-am/ProgrammeOfficiel';
import CeremonieAM from '../pages/permis-am/CeremonieAM';
import BriefingCirculation from '../pages/BriefingCirculation';
import BriefingA2 from '../pages/BriefingA2';
import Formation125Hub from '../pages/formation-125/Hub';
import Formation125Comprendre from '../pages/formation-125/Comprendre';
import Formation125Maitriser from '../pages/formation-125/Maitriser';
import Formation125Anticiper from '../pages/formation-125/Anticiper';
import QuizHost from '../pages/QuizHost';
import QuizStudent from '../pages/QuizStudent';
import FichePremiereCirculationA2 from '../pages/fiches/FichePremiereCirculationA2';
import PlateauParcoursA2 from '../pages/fiches/PlateauParcoursA2';
import PremiereCirculationEleveLanding from '../pages/fiches/PremiereCirculationEleveLanding';
import FicheLivretProgressionPlateauA2 from '../pages/fiches/FicheLivretProgressionPlateauA2';
import LivretProgressionEleveLanding from '../pages/fiches/LivretProgressionEleveLanding';
import LivretInteractifPlateauA2 from '../pages/fiches/LivretInteractifPlateauA2';

export const StudentRoutes = () => (
  <>
    {/* === Public élèves (StudentOnlyRoute) === */}
    <Route path="/" element={withStudentLayout(Home)} />
    <Route path="/formation" element={withStudentLayout(Formation)} />
    <Route path="/parcours" element={withStudentLayout(EspaceEleve)} />
    <Route path="/profil" element={withStudentLayout(EspaceEleve)} />
    <Route path="/compte" element={withStudentLayout(EspaceEleve)} />
    <Route path="/revision" element={withStudentLayout(MagazineHub)} />
    <Route path="/mon-livret-moto" element={withStudentLayout(MonLivretMoto)} />
    <Route path="/revoir" element={withStudentLayout(ARevoir)} />

    {/* === Public ouvert (withLayout) === */}
    <Route path="/quiz/host/:code" element={<QuizHost />} />
    <Route path="/quiz/:code" element={<QuizStudent />} />
    <Route path="/mobilites" element={withLayout(MobilitesUrbaines)} />
    <Route path="/assr" element={withLayout(ASSR)} />
    <Route path="/programme-etm" element={withLayout(ProgrammeETM)} />
    <Route path="/lexique" element={withLayout(Lexique)} />
    <Route path="/nouveau-format" element={withLayout(NouveauFormat)} />
    <Route path="/apprendre/:slug" element={withLayout(EtmIssuePage)} />
    <Route path="/apprendre/:parentSlug/:satelliteSlug" element={withLayout(EtmSatellitePage)} />
    <Route path="/enquete/:slug" element={withLayout(EtmIssuePage)} />
    <Route path="/enquetes" element={withLayout(EnquetesListPage)} />
    <Route path="/campagne-2026-motards-experimentes" element={withLayout(CampagneMotardsExperimentes)} />
    <Route path="/panneaux" element={withLayout(PanneauxGallery)} />
    <Route path="/magazine" element={withLayout(MagazineIndex)} />
    <Route path="/magazine/bien-choisir-casque-moto" element={withLayout(MagazineCasque)} />
    <Route path="/magazine/:slug" element={withLayout(MagazineArticlePage)} />
    <Route path="/ressources-pedagogiques" element={withLayout(RessourcesPedagogiques)} />
    <Route path="/livret-am/lecteur" element={<LivretAMLecteur />} />
    {/* ═══════ BIBLIOTHÈQUE UNIFIÉE · Hub central pour élèves & enseignants + visiteurs (avec cadenas) ═══════ */}
    <Route path="/bibliotheque" element={withLayout(Bibliotheque)} />
    {/* ═══════ ANNUAIRE moto-écoles data.gouv.fr (Phase 33) ═══════ */}
    <Route path="/annuaire" element={withLayout(Annuaire)} />
    <Route path="/annuaire/opposition/confirmer" element={withLayout(OppositionConfirmer)} />
    {/* ═══════ SEO local moto-écoles (Phase 34.A) ═══════ */}
    <Route path="/moto-ecoles" element={withLayout(MotoEcolesHub)} />
    <Route path="/moto-ecoles/:dept" element={withLayout(MotoEcolesDept)} />
    <Route path="/moto-ecoles/:dept/:villeSlug" element={withLayout(MotoEcolesVille)} />
    <Route path="/moto-ecoles/:dept/:villeSlug/:schoolId/gerer" element={withLayout(MotoEcoleGerer)} />
    <Route path="/mentions-annuaire" element={withLayout(MentionsAnnuaire)} />
    <Route path="/plateau-progression" element={withLayout(PlateauProgression)} />
    <Route path="/formation-125-progression" element={withLayout(Formation125Progression)} />
    <Route path="/passerelle-a2-a-progression" element={withLayout(PasserelleA2AProgression)} />
    <Route path="/rapport-criteres" element={withLayout(RapportCriteres)} />
    <Route path="/formations-2-roues" element={withLayout(Formations2Roues)} />
    <Route path="/trames-pedagogiques" element={<Navigate to="/formations-2-roues" replace />} />
    {/* Anciennes URLs (redirection permanente pour éviter les liens cassés QR/impression) */}
    <Route path="/plateau-drive-school" element={<Navigate to="/plateau-progression" replace />} />
    <Route path="/plateau/progression-drive-school" element={<Navigate to="/plateau-progression" replace />} />
    {/* ═══════ COURS PROTÉGÉS · Réservés aux comptes Élève / Enseignant / Admin ═══════ */}
    {/* Livrets interactifs */}
    <Route path="/livret-plateau" element={withLayout(LivretPlateauBookPublic)} />
    <Route path="/livret-plateau/print" element={<LivretPlateauPrint />} />
    <Route path="/livret-circulation" element={withLayout(LivretCirculationBookPublic)} />
    <Route path="/livret-circulation/print" element={<LivretCirculationPrint />} />
    <Route path="/livret-125" element={withLayout(Livret125)} />
    <Route path="/livret-am" element={withLayout(LivretAM)} />
    <Route path="/livret-passerelle-a2-a" element={withLayout(LivretPasserelleA2A)} />
    {/* Cours A2 · Circulation + Plateau — accès élève/enseignant/admin */}
    <Route path="/cours/premiere-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><PremiereCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/deuxieme-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><DeuxiemeCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/troisieme-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><TroisiemeCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/quatrieme-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><QuatriemeCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/cinquieme-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><CinquiemeCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/sixieme-circulation-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><SixiemeCirculationA2 /></ProtectedRoute>} />
    <Route path="/cours/premiere-plateau-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><PremierePlateauA2 /></ProtectedRoute>} />
    <Route path="/cours/deuxieme-plateau-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><DeuxiemePlateauA2 /></ProtectedRoute>} />
    <Route path="/cours/troisieme-plateau-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><TroisiemePlateauA2 /></ProtectedRoute>} />
    <Route path="/cours/quatrieme-plateau-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><QuatriemePlateauA2 /></ProtectedRoute>} />
    <Route path="/permis-am" element={<PermisAmHub />} />
    <Route path="/permis-am/programme" element={<ProgrammeOfficielAM />} />
    {/* Option Quadricycle léger à moteur (voiturette / VSP) — accès réservé aux comptes connectés
        (aligné avec Option 1 Cyclomoteur : Journée 1/2/Sensibilisation sont également ProtectedRoute) */}
    <Route
      path="/permis-am/voiturette"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <HubVoiturette />
        </ProtectedRoute>
      }
    />
    <Route
      path="/permis-am/voiturette/:slug"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <SequenceVoiturette />
        </ProtectedRoute>
      }
    />
    <Route path="/permis-am/themes" element={<ThemesAM />} />
    <Route
      path="/livrets/topolino-am"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <LivretTopolinoAM />
        </ProtectedRoute>
      }
    />
    <Route
      path="/livrets/parcours-am"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <LivretParcoursAM />
        </ProtectedRoute>
      }
    />
    <Route
      path="/livrets/voiturette-8h"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <LivretVoiturette8h />
        </ProtectedRoute>
      }
    />
    <Route
      path="/permis-am/ceremonie"
      element={
        <ProtectedRoute roles={['enseignant', 'eleve', 'admin']}>
          <CeremonieAM />
        </ProtectedRoute>
      }
    />
    {/* Cours AM · Journée 1 · Journée 2 · Sensibilisation */}
    <Route path="/cours/permis-am-journee-1" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><PermisAmJournee1 /></ProtectedRoute>} />
    <Route path="/cours/permis-am-journee-2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><PermisAmJournee2 /></ProtectedRoute>} />
    <Route path="/cours/permis-am-sensibilisation" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><PermisAmSensibilisation /></ProtectedRoute>} />
    <Route path="/briefing-circulation" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><BriefingCirculation /></ProtectedRoute>} />
    <Route path="/briefing-a2" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><BriefingA2 /></ProtectedRoute>} />
    {/* Formation 125 · hub public, cours protégés */}
    <Route path="/formation-125" element={<Formation125Hub />} />
    <Route path="/formation-125/comprendre" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><Formation125Comprendre /></ProtectedRoute>} />
    <Route path="/formation-125/maitriser" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><Formation125Maitriser /></ProtectedRoute>} />
    <Route path="/formation-125/anticiper" element={<ProtectedRoute roles={['enseignant','eleve','admin']}><Formation125Anticiper /></ProtectedRoute>} />
    <Route path="/videos" element={withLayout(Videos)} />
    <Route path="/videos/conditions" element={withLayout(VideosConditionsPage)} />
    {/* Shorts : plateforme vidéos verticales (Phase 36) — pas de layout car plein écran */}
    <Route path="/shorts" element={<ShortsPage />} />
    <Route path="/shorts/proposer" element={withLayout(ShortsProposerPage)} />
    <Route path="/demos-interactives" element={<DemosInteractives />} />
    <Route path="/demos" element={<DemosInteractives />} />
    <Route path="/livret" element={<ProtectedRoute roles={['enseignant','eleve','admin']}>{withLayout(LivretApprentissageA)}</ProtectedRoute>} />
    <Route path="/seance-recap" element={withLayout(SeanceRecap)} />
    {/* Page publique de partage d'un bilan via QR (Phase 30.C — planning perso) */}
    <Route path="/partage/:token" element={<PartagePage />} />
    {/* Page publique de rattachement à un moniteur via QR (Phase 31) */}
    <Route path="/moniteur/qr/:token" element={<MoniteurQrPage />} />
    {/* Fiches pédagogiques imprimables (Phase 31) */}
    {/* Redirection : la fiche 1ère leçon plateau est fusionnée dans le livret de progression */}
    <Route path="/fiches/moto/premiere-lecon-a2" element={<Navigate to="/fiches/moto/livret-progression-plateau-a2" replace />} />
    <Route path="/fiches/moto/plateau-parcours-a2" element={<PlateauParcoursA2 />} />
    <Route path="/fiches/moto/premiere-circulation-a2" element={<FichePremiereCirculationA2 />} />
    <Route path="/fiches/moto/livret-progression-plateau-a2" element={<FicheLivretProgressionPlateauA2 />} />
    {/* Landings élève dédiées (destination du QR partagé par le moniteur) */}
    <Route path="/fiches/eleve/premiere-lecon-a2" element={<Navigate to="/fiches/eleve/livret-plateau-a2" replace />} />
    <Route path="/fiches/eleve/premiere-circulation-a2" element={<PremiereCirculationEleveLanding /> } />
    <Route path="/fiches/eleve/livret-progression-plateau-a2" element={<LivretProgressionEleveLanding />} />
    <Route path="/fiches/eleve/livret-plateau-a2" element={<LivretInteractifPlateauA2 />} />
    <Route path="/eleve/livret-plateau" element={<LivretInteractifPlateauA2 />} />
    {/* /preparation-ccs : contenu pédagogique CCS ECSR — réservé aux enseignants
        et admins. Redirige vers /login avec state.from = chemin courant. */}
    <Route
      path="/preparation-ccs"
      element={
        <ProtectedRoute roles={['enseignant', 'admin']}>
          {withLayout(PreparationCCS)}
        </ProtectedRoute>
      }
    />

    {/* === Slugs SEO courts === */}
    <Route path="/casque-moto-ece-22-06" element={withLayout(SeoShortRoute)} />
    <Route path="/permis-a1-a2" element={withLayout(SeoShortRoute)} />
    <Route path="/equipement-moto-obligatoire" element={withLayout(SeoShortRoute)} />
    <Route path="/gants-moto-homologues" element={withLayout(SeoShortRoute)} />
    <Route path="/trajectoires-moto-securite" element={withLayout(SeoShortRoute)} />
    <Route path="/distances-securite-moto" element={withLayout(SeoShortRoute)} />
    <Route path="/alcool-stupefiants-moto" element={withLayout(SeoShortRoute)} />
    <Route path="/plateau-moto" element={withLayout(EpreuvesPlateau)} />

    {/* === Pages piliers SEO === */}
    <Route path="/code-moto-gratuit" element={withLayout(PillarCodeMotoGratuit)} />
    <Route path="/permis-a1" element={withLayout(PillarPermisA1)} />
    <Route path="/permis-a2" element={withLayout(PillarPermisA2)} />
    <Route path="/bsr-am" element={withLayout(PillarBsrAm)} />
    <Route path="/circulation-moto" element={withLayout(PillarCirculationMoto)} />
    <Route path="/passerelle-a2-a" element={withLayout(PillarPasserelleA2A)} />
    <Route path="/125-permis-b" element={withLayout(PillarPermis125PermisB)} />
    <Route path="/l5e-ryker" element={withLayout(PillarL5eRyker)} />

    {/* === Légal === */}
    <Route path="/mentions-legales" element={withLayout(MentionsLegales)} />
    <Route path="/charte-editoriale" element={withLayout(CharteEditoriale)} />
    <Route path="/confidentialite" element={withLayout(Confidentialite)} />
    <Route path="/contact" element={withLayout(Contact)} />
    <Route path="/newsletter" element={withLayout(Newsletter)} />
    <Route path="/newsletter/desinscription" element={withLayout(NewsletterUnsubscribe)} />
    <Route path="/sanctions-baremes" element={withLayout(SanctionsBaremes)} />
    <Route path="/ressources-officielles" element={withLayout(RessourcesOfficielles)} />

    {/* === Épreuves pratiques (mixte élève + enseignant) === */}
    <Route path="/epreuves" element={withLayout(EpreuvesHub)} />
    <Route path="/epreuves/plateau" element={withLayout(EpreuvesPlateau)} />
    <Route path="/epreuves/circulation" element={withLayout(EpreuvesCirculation)} />
    <Route path="/epreuves/trajectoires" element={withLayout(EpreuvesTrajectoires)} />

    {/* === Auth === */}
    <Route path="/login" element={withLayout(Login)} />
    <Route path="/register" element={withLayout(Register)} />
    <Route path="/mot-de-passe-oublie" element={withLayout(ForgotPassword)} />
    {/* === Rejoindre === */}
    <Route path="/rejoindre-enseignant" element={withLayout(TeacherApplication)} />

    {/* === Espace Voiture (fusion codevoiture.fr → code2roues.fr) === */}
    <Route path="/voiture" element={withLayout(VoitureHub)} />
    <Route path="/voiture/magazine" element={withLayout(VoitureMagazine)} />
    <Route path="/voiture/magazine/:slug" element={withLayout(VoitureArticle)} />
    <Route path="/voiture/apprendre/:parentSlug/:slug" element={withLayout(VoitureArticle)} />

    {/* === Back-office (Phase 29 — import codevoiture.fr) === */}
    {/* Routes protégées : la page fait sa propre vérification `getMe` et redirige vers /login si nécessaire */}
    <Route path="/bo" element={withLayout(BoHubPage)} />
    <Route path="/bo/secretariat/eleves" element={withLayout(BoSecretariatElevesPage)} />
    <Route path="/bo/secretariat/enseignants" element={withLayout(BoSecretariatEnseignantsPage)} />
    <Route path="/bo/secretariat/planning" element={withLayout(BoSecretariatPlanningPage)} />
    <Route path="/bo/mon-planning" element={withLayout(BoMyPlanningPage)} />
    <Route path="/bo/eleve" element={withLayout(BoEleveDashboardPage)} />
    <Route path="/bo/admin/inbox" element={withLayout(BoAdminInboxPage)} />

    {/* === Redirections legacy === */}
    <Route path="/examen" element={<Navigate to="/revision" replace />} />
    <Route path="/premium" element={<Navigate to="/" replace />} />
    <Route path="/premium/success" element={<Navigate to="/" replace />} />
  </>
);

export default StudentRoutes;
